import type { SerializedError } from '@reduxjs/toolkit';
import type { ColumnState } from 'ag-grid-community';

import type { BlotterFilterPresetRange } from '@/store/blotter/daterangeCustomPresets.ts';
import type { BlotterAccumulatorEntry } from '@/store/blotter/types/BlotterAccumulatorEntry.ts';
import type { BlotterCashEntry } from '@/store/blotter/types/BlotterCashEntry.ts';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import type { ISODate } from '@/types/date.ts';

export type BlotterFilter =
  // only one filter for now (date filter)
  // for the 'onlyMyDeals' filter, see UserPreferencesData.showMyMainDeals
  | {
      type: 'intraday';
    }
  | { type: 'historical'; subtype: 'preset'; preset: BlotterFilterPresetRange }
  | {
      type: 'historical';
      subtype: 'custom';
      range: {
        fromDate?: ISODate;
        toDate?: ISODate;
      };
    };

type BlotterCommon = {
  // this state part is to be kept when we go from the 'registered' state to the 'unregistered' state
  // allowing the user to retrieve its settings when going to the 'registered' state again
  filter: BlotterFilter;
  panelIsOpen: boolean;
  panelHeight: number;
  activeTab: BlotterType;
  columnState: Record<BlotterType, ColumnState[]>;
  entries: BlotterEntriesMaps;
};

type BlotterUnregistered = { status: 'unregistered' } & BlotterCommon;
type BlotterRegistering = { status: 'registering' } & BlotterCommon;

type BlotterRegistrationFailed = {
  status: 'registration failed';
  reason: SerializedError;
} & BlotterCommon;

type BlotterImpairment = 'initial trades not loaded' | 'initial orders not loaded';

export const blotterTypes = ['Cash', 'Option', 'Order', 'Accumulator'] as const;
export type BlotterType = (typeof blotterTypes)[number];

export type BlotterEntriesMaps = {
  Cash: Record<string, BlotterCashEntry>;
  Option: Record<string, BlotterOptionEntry>;
  Accumulator: Record<string, BlotterAccumulatorEntry>;
  Order: Record<string, BlotterOrderEntry>;
};

type BlotterRegistered = {
  status: 'registered';
  // impairments :
  // if set and non empty : blotter is operating but with impairements: must be displayed to the user somehow
  impairments?: BlotterImpairment[];
} & BlotterCommon;

export type BlotterState = BlotterUnregistered | BlotterRegistering | BlotterRegistrationFailed | BlotterRegistered;
